import React, { lazy, Suspense } from 'react';
import '@heartlandone/vega/style/vega.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import KeycloakAuth from './KeycloakAuth';
import Layout from 'Containers/Layout/Layout';
import NotFound from 'Containers/NotFound/NotFound';
import { menuHandle, barToHump } from 'Chores/utils';
import { FE_ROUTES } from 'Chores/Api';
import ScrollTop from 'Components/ScrollTop';
import './App.scss';
import BackToSharkTankAfterLogin from 'Containers/BackToSharkTankAfterLogin';
import Home from './Containers/Home';
import PasswordReset from './Containers/PasswordReset/PasswordReset';
import '@heartlandone-private/fontawesome-pro/css/fontawesome.min.css';
import '@heartlandone-private/fontawesome-pro/css/regular.min.css';
import '@heartlandone-private/fontawesome-pro/css/duotone.min.css';
import { VegaThemeManager } from '@heartlandone/vega';
import UserProfile from './Containers/UserManagement/UserProfile/UserProfile';
import Message from './Containers/MessageBox/index';

// Must match the PUBLIC_PATHS list in index.js
const PUBLIC_PATHS = ['/reset-password'];
const isPublicRoute = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

function App() {
  VegaThemeManager.applyBranding('gp');

  // On public routes, render only public pages — no Keycloak-dependent components.
  if (isPublicRoute) {
    return (
      <Router>
        <ScrollTop>
          <Helmet
            titleTemplate="%s - globalpayment"
            defaultTitle="globalpayment"
            htmlAttributes={{ lang: 'i18n.language' }}
          >
            <meta name="description" content="globalpayment" />
          </Helmet>
          <Routes>
            <Route path={FE_ROUTES.RESET_PASSWORD} element={<PasswordReset />} />
            <Route path="*" element={<Navigate to={FE_ROUTES.RESET_PASSWORD} replace />} />
          </Routes>
        </ScrollTop>
      </Router>
    );
  }

  const menus = useSelector((state) => state.common.menus);
  const routeMenus = menuHandle(menus);
  const lazyLoad = (moduleName, menuProps) => {
    const Module = lazy(() => import(`./Containers/${moduleName}`));
    return <Module menuProps={menuProps} />;
  };

  // if component has descendant <Routes>  its path should be end with /* and handle not found page in its self <Routes>
  const hasSubRoutesComponents = [
    'BillingCenter',
    'DevCharts',
    'DevMetrics',
    'WorkspaceRegistration',
    'Permissions/Module',
    'Permissions/Project',
    'QaManagement',
    'Retro',
    'SprintGrid',
    'CostCenter',
    'Requests',
    'RequestHandling',
    'RequestGroupHandling',
    'MenuManagement',
    'WorkspacePermission',
    'UserManagement',
    'CDSResource'
  ];

  return (
    <Router>
      <ScrollTop>
        <Helmet
          titleTemplate="%s - globalpayment"
          defaultTitle="globalpayment"
          htmlAttributes={{ lang: 'i18n.language' }}
        >
          <meta name="description" content="globalpayment" />
        </Helmet>
        <Routes>
          <Route path={FE_ROUTES.KEYCLOAK_AUTH} element={<KeycloakAuth />} />
          <Route path={FE_ROUTES.LOGINJUMP} element={<BackToSharkTankAfterLogin />} />
          <Route path={FE_ROUTES.HOME} element={<Layout />}>
            <Route path={FE_ROUTES.HOME} element={<Home />} />
            <Route path={FE_ROUTES.HOME + '/profile'} element={<UserProfile />} />
            <Route path={FE_ROUTES.HOME + '/message'} element={<Message />} />
            {routeMenus?.map((item, key) => {
              const componentName = barToHump(item.component);
              let path = FE_ROUTES.HOME + item.newPath;
              if (hasSubRoutesComponents.includes(componentName)) {
                path += '/*';
              }
              return item.component ? (
                <Route key={key} path={path} element={<Suspense>{lazyLoad(componentName, item)}</Suspense>}></Route>
              ) : (
                ''
              );
            })}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ScrollTop>
    </Router>
  );
}

export default App;
